import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getDailyPuzzles, getPuzzleStats, completePuzzle } from '../lib/api';
import { SKETCH_PATTERNS } from '../lib/images';
import { Loader2, Brain, BookOpen, Puzzle as PuzzleIcon, Grid3X3, Trophy, Clock, Star } from 'lucide-react';

const puzzleIcons = {
  memory_flip: Grid3X3,
  zen_pattern: Brain,
  story_completion: BookOpen,
  sliding_tile: PuzzleIcon,
};

export default function PuzzlesPage() {
  const { deviceId } = useAuth();
  const [puzzles, setPuzzles] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [puzzleRes, statsRes] = await Promise.allSettled([
          getDailyPuzzles(),
          getPuzzleStats(deviceId),
        ]);
        if (puzzleRes.status === 'fulfilled') setPuzzles(puzzleRes.value.data?.puzzles || []);
        if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      } catch {}
      setLoading(false);
    }
    load();
  }, [deviceId]);

  const handleComplete = async (puzzle) => {
    try {
      await completePuzzle({
        device_id: deviceId,
        puzzle_id: puzzle.id,
        puzzle_type: puzzle.type,
        completion_time_seconds: 60,
        date: new Date().toISOString().split('T')[0],
      });
      const statsRes = await getPuzzleStats(deviceId);
      setStats(statsRes.data);
      setSelected(null);
      setPlaying(false);
    } catch {}
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary)' }} />
    </div>
  );

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-12 max-w-5xl mx-auto" data-testid="puzzles-page">
      {/* Sketch-style header */}
      <div className="rounded-lg overflow-hidden mb-8 relative h-36"
        style={{ background: 'linear-gradient(135deg, #E5E1D8 0%, #D4CBB3 50%, #F2EFE9 100%)' }}>
        <div className="absolute inset-0 opacity-12" style={{ backgroundImage: `url("${SKETCH_PATTERNS.zenStones}")`, backgroundSize: '200px', backgroundPosition: 'right center', backgroundRepeat: 'no-repeat' }} />
        <div className="absolute inset-0 flex items-center px-8">
          <div>
            <h1 className="text-3xl font-bold mb-1" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Daily Puzzles</h1>
            <p className="text-sm" style={{ color: 'var(--muted-fg)' }}>Exercise your mind with today's brain teasers</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="flex gap-4 mb-8 flex-wrap">
          <div className="rounded-lg p-4 border flex-1 min-w-[120px]" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <Trophy className="w-5 h-5 mb-1" style={{ color: 'var(--accent)' }} />
            <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{stats.total_points || 0}</p>
            <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>Total Points</p>
          </div>
          <div className="rounded-lg p-4 border flex-1 min-w-[120px]" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <Star className="w-5 h-5 mb-1" style={{ color: 'var(--primary)' }} />
            <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{stats.puzzles_completed || 0}</p>
            <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>Completed</p>
          </div>
          <div className="rounded-lg p-4 border flex-1 min-w-[120px]" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <Clock className="w-5 h-5 mb-1" style={{ color: 'var(--secondary-fg)' }} />
            <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{stats.streak || 0}</p>
            <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>Day Streak</p>
          </div>
        </div>
      )}

      {/* Puzzle selection or playing */}
      {!selected ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {puzzles.map((puzzle, i) => {
            const Icon = puzzleIcons[puzzle.type] || PuzzleIcon;
            return (
              <button key={puzzle.id} onClick={() => { setSelected(puzzle); setPlaying(true); }}
                className={`rounded-lg p-6 border text-left transition-all hover:-translate-y-1 hover:shadow-lg animate-fade-in stagger-${i + 1}`}
                style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid={`puzzle-card-${i}`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ background: puzzle.gradient?.[0] || 'var(--muted)' }}>
                    <Icon className="w-6 h-6" style={{ color: 'var(--primary)' }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold mb-0.5" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{puzzle.title}</h3>
                    <p className="text-xs mb-1" style={{ color: 'var(--primary)' }}>{puzzle.subtitle}</p>
                    <p className="text-sm" style={{ color: 'var(--muted-fg)' }}>{puzzle.description}</p>
                    <div className="flex items-center gap-3 mt-3 text-xs" style={{ color: 'var(--muted-fg)' }}>
                      <span>{puzzle.difficulty}</span>
                      <span>{puzzle.estimated_time}</span>
                      <span className="flex items-center gap-1"><Star className="w-3 h-3" /> {puzzle.points} pts</span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="animate-fade-in">
          <button onClick={() => { setSelected(null); setPlaying(false); }} className="text-sm mb-4 flex items-center gap-1" style={{ color: 'var(--primary)' }}>
            Back to puzzles
          </button>

          <div className="rounded-lg p-8 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="puzzle-play-area">
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{selected.title}</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--muted-fg)' }}>{selected.description}</p>

            {/* Memory Flip */}
            {selected.type === 'memory_flip' && selected.data?.cards && (
              <MemoryFlipGame cards={selected.data.cards} onComplete={() => handleComplete(selected)} />
            )}

            {/* Zen Pattern */}
            {selected.type === 'zen_pattern' && selected.data && (
              <ZenPatternGame data={selected.data} onComplete={() => handleComplete(selected)} />
            )}

            {/* Story Completion */}
            {selected.type === 'story_completion' && selected.data && (
              <StoryGame data={selected.data} onComplete={() => handleComplete(selected)} />
            )}

            {/* Sliding Tile - simplified */}
            {selected.type === 'sliding_tile' && (
              <div className="text-center py-8">
                <PuzzleIcon className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--muted)' }} />
                <p className="text-sm mb-4" style={{ color: 'var(--muted-fg)' }}>Sliding tile puzzle</p>
                <button onClick={() => handleComplete(selected)}
                  className="px-6 py-2 rounded-lg font-medium text-sm" data-testid="complete-puzzle-btn"
                  style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
                  Mark Complete
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Memory Flip mini-game
function MemoryFlipGame({ cards, onComplete }) {
  const [shuffled] = useState(() => [...cards].sort(() => Math.random() - 0.5));
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);

  const handleFlip = (idx) => {
    if (flipped.length === 2 || flipped.includes(idx) || matched.includes(idx)) return;
    const next = [...flipped, idx];
    setFlipped(next);
    if (next.length === 2) {
      if (shuffled[next[0]] === shuffled[next[1]]) {
        const newMatched = [...matched, ...next];
        setMatched(newMatched);
        setFlipped([]);
        if (newMatched.length === shuffled.length) setTimeout(onComplete, 500);
      } else {
        setTimeout(() => setFlipped([]), 800);
      }
    }
  };

  return (
    <div className="grid grid-cols-4 gap-3 max-w-md mx-auto" data-testid="memory-flip-grid">
      {shuffled.map((card, i) => {
        const isVisible = flipped.includes(i) || matched.includes(i);
        return (
          <button key={i} onClick={() => handleFlip(i)}
            className="aspect-square rounded-lg flex items-center justify-center text-sm font-bold transition-all"
            style={{
              background: matched.includes(i) ? 'var(--primary)' : isVisible ? 'var(--secondary)' : 'var(--muted)',
              color: matched.includes(i) ? 'var(--primary-fg)' : isVisible ? 'var(--secondary-fg)' : 'transparent',
              border: `1px solid ${isVisible ? 'var(--primary)' : 'var(--border)'}`,
            }} data-testid={`memory-card-${i}`}>
            {isVisible ? card : '?'}
          </button>
        );
      })}
    </div>
  );
}

// Zen Pattern mini-game
function ZenPatternGame({ data, onComplete }) {
  const [answer, setAnswer] = useState(null);
  const correct = answer === data.answer;

  return (
    <div className="text-center" data-testid="zen-pattern-game">
      <div className="flex items-center justify-center gap-3 mb-6 flex-wrap">
        {data.sequence.map((s, i) => (
          <span key={i} className="px-4 py-2 rounded-lg text-sm font-medium" style={{ background: 'var(--muted)', color: 'var(--text)' }}>
            {s}
          </span>
        ))}
        <span className="px-4 py-2 rounded-lg text-sm font-bold" style={{ background: answer ? (correct ? 'var(--primary)' : 'var(--accent)') : 'var(--secondary)', color: answer ? 'white' : 'var(--text)' }}>
          {answer || '?'}
        </span>
      </div>
      <p className="text-sm mb-4" style={{ color: 'var(--muted-fg)' }}>What comes next?</p>
      <div className="flex items-center justify-center gap-3">
        {data.options.map(opt => (
          <button key={opt} onClick={() => { setAnswer(opt); if (opt === data.answer) setTimeout(onComplete, 1000); }}
            className="px-6 py-2.5 rounded-lg font-medium text-sm transition-all hover:opacity-90"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
            data-testid={`pattern-option-${opt}`}>
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

// Story completion mini-game
function StoryGame({ data, onComplete }) {
  const [answer, setAnswer] = useState(null);
  const correct = answer === data.answer;

  return (
    <div className="text-center" data-testid="story-game">
      <p className="text-lg mb-6 leading-relaxed" style={{ color: 'var(--text)', fontFamily: 'Manrope' }}>
        {data.story}
      </p>
      <div className="flex items-center justify-center gap-3 flex-wrap">
        {data.options.map(opt => (
          <button key={opt} onClick={() => { setAnswer(opt); if (opt === data.answer) setTimeout(onComplete, 1000); }}
            className="px-6 py-2.5 rounded-lg font-medium text-sm transition-all"
            style={{
              background: answer === opt ? (correct ? 'var(--primary)' : 'var(--accent)') : 'var(--surface)',
              color: answer === opt ? 'white' : 'var(--text)',
              border: `1px solid ${answer === opt ? 'transparent' : 'var(--border)'}`,
            }}
            data-testid={`story-option-${opt}`}>
            {opt}
          </button>
        ))}
      </div>
      {correct && data.continuation && (
        <p className="mt-6 text-sm animate-fade-in" style={{ color: 'var(--primary)' }}>{data.continuation}</p>
      )}
    </div>
  );
}
